import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStateMachine } from 'little-state-machine';
import {
  // Testimonial,
  Button,
} from 'Components';
import { ActionsProps, FormDataProps } from './types';
import updateFormAction from 'utils/updateFormAction';
import { signupUser, trackEvent } from 'v6/api/signup';
import {
  ActionsWrapper,
  ButtonWrap,
  SignInLinkWrapper,
  SignInLink,
  SignInLinkText,
  SignInLinkTextMobile,
} from 'Pages/Homepage/styled';
import { HeartHandIcon } from 'assets/icons';
import styled from 'styled-components';
import { bugsnagNotify } from 'utils/error';
import { openSupportChat } from 'utils/open.support.chat';

export function Actions({ isValidForm, dirtyFields, handleSubmit, setError }: ActionsProps) {
  const history = useHistory();
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const { actions, state } = useStateMachine({ updateFormAction });
  const defaultValuesFromStorage = state as FormDataProps;
  const { user } = defaultValuesFromStorage;

  const onSubmit = async (data: FormDataProps) => {
    trackEvent({
      event: 'sign_up-create_login-click-next',
    });

    // TODO if user navigates back from page two, don't resubmit using the same email
    // noting that defaultValuesFromStorage may exist due to prefill from URL query param
    try {
      setError('');
      setIsSubmittingForm(true);
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      const userInfo = {
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        email: data.email,
        password: data.password,
        numberAccounts: data.numberAccounts,
        country: data.country,
        timezone: timeZone,
        _id: undefined,
        cbSubId: undefined,
      };
      // user is updating signup so we should pass in the _id we have already received
      if (user?._id) {
        // @ts-ignore
        userInfo._id = user._id;
      }
      // stored here not in user object for some reason?
      if (defaultValuesFromStorage?.cbSubId) {
        // @ts-ignore
        userInfo.cbSubId = defaultValuesFromStorage.cbSubId;
      }
      const { plans = [], user: serverUser = {} } = await signupUser(userInfo);

      //store form data to storage
      actions.updateFormAction({ ...data, plans, user: serverUser });
      history.push({ pathname: '/plan' });
    } catch (error: any) {
      setError(error.message);
      bugsnagNotify(error, 'Error signing up submit');
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const isDisabled = useMemo(() => {
    const requiredFields = ['firstName', 'lastName', 'companyName', 'email', 'password', 'tos'];

    return !requiredFields.every((key: string) => !!dirtyFields[key]) || isSubmittingForm;
  }, [dirtyFields, isSubmittingForm]);

  return (
    <ActionsWrapper>
      <ButtonWrap>
        <Button
          data-testid="continueButton"
          disabled={isDisabled}
          onClick={handleSubmit(onSubmit)}
          title={
            !isValidForm
              ? 'Please answer all the questions to the left of the screen to continue.'
              : 'Next step'
          }
        >
          {isSubmittingForm ? 'Please wait...' : 'Continue'}
        </Button>
      </ButtonWrap>

      <SignInLinkWrapper>
        Already have a Sked account?{' '}
        <SignInLink
          href="/signin"
          onClick={() => {
            trackEvent({
              event: 'sign_up-create_login-sign_in_here',
              callbackFn: () => {
                window.location.href = '/signin';
              },
            });
          }}
        >
          <SignInLinkText>Sign in</SignInLinkText>
          <SignInLinkTextMobile>Log into your account using the Desktop app.</SignInLinkTextMobile>
        </SignInLink>
      </SignInLinkWrapper>

      <ContactSection />

      {sessionStorage.getItem('skipCreditCardDetail') === 'true' && (
        <span className="noCCRequired">No Credit Card Required!</span>
      )}
    </ActionsWrapper>
  );
}
const LaunchIntercomChat = () => {
  openSupportChat(
    "I'd like some help signing up for a Sked account. I'm currently filling in my details",
  );
};

export const ContactSection = () => (
  <ContactText>
    Any questions?{' '}
    <ContactSupportLink href="#" onClick={LaunchIntercomChat}>
      Contact Support
    </ContactSupportLink>{' '}
    <HeartHandIcon />
  </ContactText>
);

const ContactText = styled.div`
  margin-top: 16px;
  color: var(--Neutral-N500, #808080);
  /* Web App/Paragraph 2 Regular */
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  display: flex;
  align-items: center;
`;
const ContactSupportLink = styled.a`
  color: var(--Primary-P500, #4f31ff);
  /* Web App/Paragraph 2 Regular */
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  margin: 0 4px;
`;
